import { useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { FileText, Upload } from 'lucide-react';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { useToast } from '@/components/ui/use-toast';
import leadService from '@/services/leadService';

export const CATALOG_META_QUERY_KEY = ['admin-catalog-meta'];

/**
 * Card "Catálogo corporativo": sube o reemplaza el PDF del catálogo sin
 * redeploy (HU-14). Al cambiarlo invalida el estado público para que el lead
 * magnet de la home sirva la versión vigente.
 */
const CatalogoCard = () => {
  const { toast } = useToast();
  const queryClient = useQueryClient();
  const [file, setFile] = useState<File | null>(null);

  const { data: catalog } = useQuery({
    queryKey: CATALOG_META_QUERY_KEY,
    queryFn: () => leadService.getCatalogMeta(),
    staleTime: 5 * 60 * 1000,
  });

  const uploadMutation = useMutation({
    mutationFn: (pdf: File) => leadService.uploadCatalog(pdf),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: CATALOG_META_QUERY_KEY });
      queryClient.invalidateQueries({ queryKey: ['catalog-status'] });
      toast({ title: 'Catálogo actualizado' });
      setFile(null);
    },
    onError: () => {
      toast({
        variant: 'destructive',
        title: 'No se pudo subir el catálogo',
      });
    },
  });

  return (
    <Card>
      <CardHeader>
        <CardTitle className="flex items-center gap-2 text-brown-dark">
          <FileText className="h-5 w-5" />
          Catálogo corporativo
        </CardTitle>
      </CardHeader>
      <CardContent className="space-y-4">
        <p className="text-sm text-brown-light/80">
          {catalog
            ? `Actual: ${catalog.filename ?? 'catálogo.pdf'}`
            : 'Todavía no hay catálogo cargado.'}
        </p>
        <input
          type="file"
          accept="application/pdf,.pdf"
          aria-label="Archivo del catálogo"
          onChange={(event) => setFile(event.target.files?.[0] ?? null)}
          className="block text-sm"
        />
        <Button
          disabled={!file || uploadMutation.isPending}
          onClick={() => file && uploadMutation.mutate(file)}
        >
          <Upload className="h-4 w-4 mr-2" />
          Subir catálogo
        </Button>
      </CardContent>
    </Card>
  );
};

export default CatalogoCard;
